import { type FC, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import MainLayout from "@/pages/layouts/MainLayout";
import { PageActions } from "@/pages/layouts/components/PageActions";
import DocumentVaultList from "./components/DocumentVaultList";
import { DocumentLimitBanner } from "./components/DocumentLimitBanner";
import { KeyDocumentsUploadCard } from "./components/KeyDocumentsUploadCard";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";
import { useDocuments } from "@/shared/hooks/useDocumentStorage";
import {
  useFolders,
  useAutoGroupFolders,
  useDeleteFolder,
  useRenameFolder,
} from "@/shared/hooks/useDocumentFolders";
import { DocumentType } from "@/shared/types/documentTypes";
import { trackJourneyEvent, EventType } from "@/shared/utils/core";
import { useUploadPaywall } from "@/shared/hooks/useUploadPaywall";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import { ChevronLeft, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { FolderNameDialog } from "./components/FolderNameDialog";
import { downloadDocumentsAsZip } from "@/shared/utils/downloadDocuments";
import { enqueueSnackbar } from "notistack";

export const DocumentsPage: FC = () => {
  const [search, setSearch] = useState("");
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | "all">("all");
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFolderId = searchParams.get("folder")
    ? parseInt(searchParams.get("folder")!, 10)
    : null;

  const {
    shouldShowPaywall,
    documentCount,
    isPremium,
    isLoading: isPaywallLoading,
    freeDocumentLimit,
    remainingSlots,
  } = useUploadPaywall();
  const { person } = useAppContext();
  const queryClient = useQueryClient();

  const { data: documentsData, isLoading: isDocumentsLoading, error } = useDocuments();
  const { data: foldersData, isLoading: isFoldersLoading } = useFolders();
  const isLoading = isDocumentsLoading || isFoldersLoading;
  const autoGroupMutation = useAutoGroupFolders();
  const autoGroupCalledRef = useRef(false);

  // Auto-group on first load (catches legacy users with existing ungrouped docs).
  // Auto-filing after classification is handled by the elayne-api pipeline.
  useEffect(() => {
    if (autoGroupCalledRef.current) return;
    if (!person?.estate_id || isDocumentsLoading) return;
    const docs = documentsData?.documents || [];
    if (docs.length < 3) return;
    autoGroupCalledRef.current = true;
    autoGroupMutation.mutate(undefined, {
      onError: console.error,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.estate_id, isDocumentsLoading]);

  const handleFolderOpen = useCallback(
    (folderId: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("folder", String(folderId));
        next.delete("doc");
        return next;
      });
    },
    [setSearchParams],
  );

  const handleFolderClose = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("folder");
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    trackJourneyEvent(EventType.DOCUMENT_VAULT_ACCESSED, {
      search_query: search || undefined,
      document_type_filter: selectedDocumentType !== "all" ? selectedDocumentType : undefined,
    });
  }, [search, selectedDocumentType]);

  useEffect(() => {
    if (!person?.estate_id) return;

    const channel = supabase
      .channel(`estate_documents_${person.estate_id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "estate_documents",
          filter: `estate_id=eq.${person.estate_id}`,
        },
        (payload) => {
          console.log("Realtime document update received:", payload);
          queryClient.invalidateQueries({
            queryKey: ["documents", person.estate_id],
          });
          queryClient.invalidateQueries({
            queryKey: ["document-folders", person.estate_id],
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [person?.estate_id, queryClient]);

  const filteredDocuments = useMemo(() => {
    const allDocs = documentsData?.documents || [];
    const hasSearchOrFilter = search !== "" || selectedDocumentType !== "all";

    return allDocs.filter((doc) => {
      // Scope to active folder first (always, even during search)
      if (activeFolderId && doc.folder_id !== activeFolderId) {
        return false;
      }

      if (selectedDocumentType !== "all" && doc.document_type !== selectedDocumentType) {
        return false;
      }

      if (search) {
        const searchLower = search.toLowerCase();
        const fileName = (doc.display_name || doc.file_name || "").toLowerCase();
        const docType = (doc.document_type || "").toLowerCase();
        if (!fileName.includes(searchLower) && !docType.includes(searchLower)) {
          return false;
        }
      }

      // On main list with no search/filter, only hide documents whose folder
      // is already loaded (prevents flash where docs vanish before folder rows appear)
      if (!activeFolderId && !hasSearchOrFilter) {
        if (!doc.folder_id) return true;
        const folderLoaded = (foldersData?.folders || []).some((f) => f.id === doc.folder_id);
        return !folderLoaded;
      }

      return true;
    });
  }, [
    documentsData?.documents,
    selectedDocumentType,
    search,
    activeFolderId,
    foldersData?.folders,
  ]);

  const activeFolder = useMemo(() => {
    if (!activeFolderId || !foldersData?.folders) return null;
    return foldersData.folders.find((f) => f.id === activeFolderId) ?? null;
  }, [activeFolderId, foldersData?.folders]);

  const deleteFolderMutation = useDeleteFolder();
  const renameFolderMutation = useRenameFolder();
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);

  const handleRenameFolderFromHeader = () => {
    if (!activeFolder) return;
    setFolderDialogOpen(true);
  };

  const handleDeleteFolderFromHeader = async () => {
    if (!activeFolder) return;
    try {
      const result = await deleteFolderMutation.mutateAsync(activeFolder.id);
      const count = result.ungrouped_count ?? 0;
      enqueueSnackbar(
        `Folder ungrouped. ${count} ${count === 1 ? "file" : "files"} moved back to main list.`,
        { variant: "success", autoHideDuration: 3000 },
      );
      handleFolderClose();
    } catch {
      enqueueSnackbar("Couldn't ungroup folder. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  };

  const handleDownloadFolderFromHeader = async () => {
    if (!activeFolder || !person?.estate_id) return;
    const folderDocs = (documentsData?.documents || []).filter(
      (d) => d.folder_id === activeFolder.id,
    );
    if (folderDocs.length === 0) {
      enqueueSnackbar("No files in this folder", { variant: "info", autoHideDuration: 3000 });
      return;
    }
    try {
      await downloadDocumentsAsZip(person.estate_id, folderDocs, activeFolder.name);
    } catch {
      enqueueSnackbar("Couldn't download folder. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  };

  const handleFolderDialogSubmit = async (name: string) => {
    if (!activeFolder) return;
    try {
      await renameFolderMutation.mutateAsync({ folderId: activeFolder.id, name });
      enqueueSnackbar("Folder renamed", { variant: "success", autoHideDuration: 3000 });
      setFolderDialogOpen(false);
    } catch {
      enqueueSnackbar("Couldn't rename folder. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  };

  const handleUploadDocument = () => {
    if (shouldShowPaywall) {
      setIsPaywallOpen(true);
      return false;
    }
    return true;
  };

  return (
    <MainLayout header="Documents" actions={<PageActions />}>
      <div className="flex flex-col w-full">
        {!isPremium && !isPaywallLoading && documentCount > 0 && (
          <DocumentLimitBanner
            documentCount={documentCount}
            freeDocumentLimit={freeDocumentLimit}
            onUpgrade={() => setIsPaywallOpen(true)}
          />
        )}
        {activeFolder && (
          <div className="mb-4">
            <button
              onClick={handleFolderClose}
              className="inline-flex items-center gap-0.5 text-sm text-brand hover:underline mb-3"
            >
              <ChevronLeft className="h-4 w-4" />
              All documents
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-xl sm:text-2xl font-semibold font-sans truncate">
                {activeFolder.name}
              </h2>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-stone-100">
                    <MoreHorizontal className="h-5 w-5 text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[260px] p-2">
                  <DropdownMenuItem
                    onClick={handleRenameFolderFromHeader}
                    className="cursor-pointer h-10 px-3"
                  >
                    Rename folder
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDownloadFolderFromHeader}
                    className="cursor-pointer h-10 px-3"
                  >
                    Download all
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleDeleteFolderFromHeader}
                    className="cursor-pointer px-3 py-2 h-auto"
                  >
                    <div className="flex flex-col">
                      <span>Delete folder</span>
                      <span className="text-xs text-secondary">
                        Files move back to the main list
                      </span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <div className="flex-1 min-w-0 w-full">
            <DocumentVaultList
              documents={filteredDocuments}
              allDocuments={documentsData?.documents || []}
              isLoading={isLoading}
              error={error}
              onUploadClick={handleUploadDocument}
              searchQuery={search}
              onSearchChange={setSearch}
              selectedDocumentType={selectedDocumentType}
              onDocumentTypeChange={setSelectedDocumentType}
              remainingSlots={isPremium ? undefined : remainingSlots}
              onLimitReached={() => setIsPaywallOpen(true)}
              folders={foldersData?.folders || []}
              activeFolderId={activeFolderId}
              activeFolder={activeFolder}
              onFolderOpen={handleFolderOpen}
              onFolderClose={handleFolderClose}
            />
          </div>
          <KeyDocumentsUploadCard
            onPaywall={() => setIsPaywallOpen(true)}
            className="w-full lg:w-[280px] flex-shrink-0"
          />
        </div>
      </div>

      <PostLossPaywall
        isOpen={isPaywallOpen}
        onOpenChange={setIsPaywallOpen}
        promptLocation="documents"
        returnPath={POST_LOSS_ROUTES.DOCUMENTS}
      />

      {activeFolder && (
        <FolderNameDialog
          open={folderDialogOpen}
          onOpenChange={setFolderDialogOpen}
          mode="rename"
          initialName={activeFolder.name}
          existingNames={(foldersData?.folders || []).map((f) => f.name)}
          onSubmit={handleFolderDialogSubmit}
          isLoading={renameFolderMutation.isPending}
        />
      )}
    </MainLayout>
  );
};
